import { Box, Button } from '@mui/material';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import saveAs from 'file-saver';
import { useGet } from 'hooks/use-get';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import {
  getR2CalibrationInputFile,
  getR2CalibrationOutputFile,
} from 'views/bluviu-nutrition/features/calibrations/api/r2-calibration';
import CloneCalibrationDialog from 'views/bluviu-nutrition/features/calibrations/components/clone-calibration-dialog';
import { CalibrationsParams } from 'views/bluviu-nutrition/features/calibrations/types/calibrations-params.type';
import { useCalibrationState } from '../../../context/calibration-context';
import { CalibrationState } from '../../../types/calibration-state.enum';
import { AxiosResponse } from 'axios';

const R2CalibrationActions: React.FC<{}> = () => {
  const PAGE_NAME_ID = 'bluViuNutrition.calibrations.editCalibration.calibrationActions';
  const params = useParams<CalibrationsParams>();
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    r2CalibrationQuery: { data: calibration },
  } = useCalibrationState();

  if (calibration?.state !== CalibrationState.COMPLETED) return;

  const saveCalibrationFile = (response: AxiosResponse) => {
    const disposition = response.headers['content-disposition']?.split(';');
    const fn = disposition?.find((n: string) => n.includes('filename='));
    if (fn) {
      const filename = `${calibration.name || 'calibration'}_${fn.replace('filename=', '').replace(/"/gi, '').trim()}`;
      saveAs(response.data, filename, { autoBom: false });
    }
  };

  const { refetch: handleExport } = useGet(
    ['getR2CalibrationInputFile', params.globalSiteId],
    ({ queryKey }) => getR2CalibrationInputFile(String(queryKey[1])),
    {
      enabled: false,
    }
  );

  const { refetch: downloadOutputFile } = useGet(
    ['getR2CalibrationOutputFile', params.globalSiteId],
    ({ queryKey }) => getR2CalibrationOutputFile(String(queryKey[1])),
    {
      enabled: false,
    }
  );

  const handleExportScripts = async () => {
    const inputFile = await handleExport();
    if (inputFile.isError) {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: inputFile.error },
      });
    } else if (inputFile.data) {
      saveCalibrationFile(inputFile.data);
    }

    const outputFile = await downloadOutputFile();
    if (outputFile.isError) {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: outputFile.error },
      });
    } else if (outputFile.data) {
      saveCalibrationFile(outputFile.data);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  if (!calibration) return;

  return (
    <Box display={'flex'} flexDirection={'row'} gap={4} justifyContent={'flex-end'}>
      <Button onClick={() => handleExportScripts()}>
        <FormattedMessage id={`${PAGE_NAME_ID}.exportScript`} />
      </Button>
      <Button disabled onClick={() => handleExportCSV()}>
        <FormattedMessage id={`${PAGE_NAME_ID}.exportCSV`} />
      </Button>
      <CloneCalibrationDialog
        calibration={{
          name: calibration.name,
          id: calibration.id,
          model: calibration.model,
        }}
        button={
          <Button>
            <FormattedMessage id={`${PAGE_NAME_ID}.clone`} />
          </Button>
        }
      />
    </Box>
  );
};

export default React.memo(R2CalibrationActions);
